import styles from './constraints.module.css';

import type { ConstraintsProps } from './ConstraintsProps';

/**
 * Return the CSS classes to style the constraints around a component. To be
 * used by components that extend {@link ConstraintsProps}.
 *
 * ```
 * type MyComponentProps = ConstraintsProps & { ... };
 * function MyComponent(props: MyComponentProps) {
 *   const constraintsClassNames = getConstraintsStyles(props);
 *   return <div className={constraintsClassNames}>{props.children}</div>;
 * }
 *
 * <MyComponent widthConstraint="md" /> // Will render a div with the class for "medium" max-width.
 * ```
 *
 * @param props - The constraints props coming from a component extending
 *   {@link ConstraintsProps}.
 * @internal
 */
export function getConstraintsStyles(props: ConstraintsProps) {
  let { widthConstraint } = props;
  const constraintsStyles = widthConstraint ? [styles['size']] : [];

  if (typeof widthConstraint === 'string') {
    widthConstraint = { xs: widthConstraint };
  }

  if (widthConstraint?.xs) {
    constraintsStyles.push(styles[`xs-size-${widthConstraint.xs}`]);
  }
  if (widthConstraint?.sm) {
    constraintsStyles.push(styles[`sm-size-${widthConstraint.sm}`]);
  }
  if (widthConstraint?.md) {
    constraintsStyles.push(styles[`md-size-${widthConstraint.md}`]);
  }
  if (widthConstraint?.lg) {
    constraintsStyles.push(styles[`lg-size-${widthConstraint.lg}`]);
  }
  if (widthConstraint?.xl) {
    constraintsStyles.push(styles[`xl-size-${widthConstraint.xl}`]);
  }

  return constraintsStyles;
}
